import { useQuery } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { listForms } from './api';
import type { Block, BlockPaletteEntry, BlockType, PropSpec } from './pages-api';
import type { Path, Problem } from './blocks';

/**
 * The blocks of a page, as a list.
 *
 * Not a canvas. A landing page is a column of sections read top to bottom, and the only spatial
 * decision that matters is the order — which two buttons express as well as a drag, and which
 * works on a phone, from a keyboard and to a screen reader. `COLUMNS` is the one exception and it
 * indents, because that is the only thing side-by-side means here.
 */

export function BlockList({
  blocks,
  path,
  palette,
  problems,
  onAdd,
  onSet,
  onRemove,
  onMove,
}: {
  blocks: Block[];
  path: Path;
  palette: BlockPaletteEntry[];
  problems: Problem[];
  onAdd: (into: Path, type: BlockType) => void;
  onSet: (at: Path, key: string, value: unknown) => void;
  onRemove: (at: Path) => void;
  onMove: (at: Path, direction: -1 | 1) => void;
}): React.JSX.Element {
  /* Only the top level offers every block; a column holds content, not more columns. */
  const offered = palette.filter((entry) => path.length === 0 || !entry.nests);

  return (
    <div className="steps">
      <ol className="steps__list">
        {blocks.map((block, index) => {
          const here = [...path, index];
          const entry = palette.find((candidate) => candidate.type === block.type);

          return (
            <li key={`${block.type}-${index}`} className="steps__item">
              <BlockCard
                block={block}
                entry={entry}
                path={here}
                palette={palette}
                problems={problems}
                first={index === 0}
                last={index === blocks.length - 1}
                onAdd={onAdd}
                onSet={onSet}
                onRemove={onRemove}
                onMove={onMove}
              />
            </li>
          );
        })}
      </ol>

      <div className="steps__add">
        {offered.map((entry) => (
          <button
            key={entry.type}
            type="button"
            className="button button--quiet button--compact"
            onClick={() => onAdd(path, entry.type)}
          >
            {`+ ${t(`blocks.${entry.type}`)}`}
          </button>
        ))}
      </div>
    </div>
  );
}

function BlockCard({
  block,
  entry,
  path,
  palette,
  problems,
  first,
  last,
  onAdd,
  onSet,
  onRemove,
  onMove,
}: {
  block: Block;
  entry: BlockPaletteEntry | undefined;
  path: Path;
  palette: BlockPaletteEntry[];
  problems: Problem[];
  first: boolean;
  last: boolean;
  onAdd: (into: Path, type: BlockType) => void;
  onSet: (at: Path, key: string, value: unknown) => void;
  onRemove: (at: Path) => void;
  onMove: (at: Path, direction: -1 | 1) => void;
}): React.JSX.Element {
  const key = path.join('.');
  const mine = problems.filter((problem) => problem.path.join('.') === key);

  return (
    <div className={`step step--action${mine.length > 0 ? ' step--wrong' : ''}`}>
      <header className="step__head">
        <span className="strong small">{t(`blocks.${block.type}`)}</span>

        <span className="step__tools">
          <button
            type="button"
            className="button button--quiet button--compact"
            disabled={first}
            aria-label={t('automations.moveUp')}
            onClick={() => onMove(path, -1)}
          >
            ↑
          </button>
          <button
            type="button"
            className="button button--quiet button--compact"
            disabled={last}
            aria-label={t('automations.moveDown')}
            onClick={() => onMove(path, 1)}
          >
            ↓
          </button>
          <button
            type="button"
            className="chip__remove"
            aria-label={t('pages.removeBlock')}
            onClick={() => onRemove(path)}
          >
            ×
          </button>
        </span>
      </header>

      <div className="stack">
        {(entry?.props ?? []).map((prop) => (
          <PropField
            key={prop.key}
            spec={prop}
            value={block.props[prop.key]}
            onChange={(value) => onSet(path, prop.key, value)}
          />
        ))}
      </div>

      {entry?.nests && (
        <section className="step__branch">
          <h4 className="record__subhead">{t('pages.inside')}</h4>
          <BlockList
            blocks={block.children ?? []}
            path={path}
            palette={palette}
            problems={problems}
            onAdd={onAdd}
            onSet={onSet}
            onRemove={onRemove}
            onMove={onMove}
          />
        </section>
      )}

      {mine.length > 0 && (
        <ul className="step__problems">
          {mine.map((problem) => (
            <li key={problem.key} className="small">
              {t('pages.problem.required').replace('{field}', t(`blocks.prop.${problem.key}`))}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** One prop of a block, drawn the way the palette's `kind` asks for. */
function PropField({
  spec,
  value,
  onChange,
}: {
  spec: PropSpec;
  value: unknown;
  onChange: (value: unknown) => void;
}): React.JSX.Element {
  const label = (
    <span>
      {t(`blocks.prop.${spec.key}`)}
      {spec.required && <span className="required"> *</span>}
    </span>
  );

  const forms = useQuery({
    queryKey: ['forms'],
    queryFn: () => listForms(),
    enabled: spec.kind === 'form',
    staleTime: 5 * 60 * 1000,
  });

  const text = typeof value === 'string' ? value : '';

  if (spec.kind === 'form') {
    return (
      <label className="stack__field">
        {label}
        <select className="select" value={text} onChange={(event) => onChange(event.target.value)}>
          <option value="">{t('pages.pickForm')}</option>
          {/* A draft form has no public address, so embedding one would embed a dead link. */}
          {(forms.data?.forms ?? [])
            .filter((form) => form.status === 'PUBLISHED')
            .map((form) => (
              <option key={form.id} value={form.id}>
                {form.name}
              </option>
            ))}
        </select>
      </label>
    );
  }

  if (spec.kind === 'level') {
    return (
      <label className="stack__field">
        {label}
        <select
          className="select"
          value={typeof value === 'number' ? String(value) : '2'}
          onChange={(event) => onChange(Number(event.target.value))}
        >
          {[1, 2, 3, 4, 5, 6].map((level) => (
            <option key={level} value={level}>
              {`H${level}`}
            </option>
          ))}
        </select>
      </label>
    );
  }

  if (spec.kind === 'align') {
    return (
      <label className="stack__field">
        {label}
        <select className="select" value={text} onChange={(event) => onChange(event.target.value)}>
          <option value="">{t('pages.align.default')}</option>
          {(['left', 'center', 'right'] as const).map((align) => (
            <option key={align} value={align}>
              {t(`pages.align.${align}`)}
            </option>
          ))}
        </select>
      </label>
    );
  }

  if (spec.kind === 'boolean') {
    return (
      <label className="cell-inline">
        <input
          type="checkbox"
          checked={value === true}
          onChange={(event) => onChange(event.target.checked)}
        />
        {label}
      </label>
    );
  }

  if (spec.kind === 'money') {
    // Typed in reais, stored in centavos. One conversion, at the edge, as everywhere else.
    return (
      <label className="stack__field">
        {label}
        <input
          inputMode="decimal"
          value={typeof value === 'number' ? (value / 100).toFixed(2).replace('.', ',') : ''}
          placeholder="0,00"
          onChange={(event) => {
            const parsed = Number(event.target.value.replace(/\./g, '').replace(',', '.'));

            onChange(Number.isFinite(parsed) ? Math.round(parsed * 100) : 0);
          }}
        />
      </label>
    );
  }

  if (spec.kind === 'number') {
    return (
      <label className="stack__field">
        {label}
        <input
          type="number"
          min={0}
          max={400}
          value={typeof value === 'number' ? value : ''}
          onChange={(event) =>
            onChange(event.target.value === '' ? undefined : Number(event.target.value))
          }
        />
      </label>
    );
  }

  if (spec.kind === 'richtext') {
    return (
      <label className="stack__field">
        {label}
        <textarea
          rows={3}
          value={text}
          placeholder={t('pages.richtextHint')}
          onChange={(event) => onChange(event.target.value)}
        />
        <span className="muted small">{t('pages.richtextHint')}</span>
      </label>
    );
  }

  if (spec.kind === 'list') {
    const items = Array.isArray(value) ? (value as string[]) : [];

    return (
      <div className="stack__field">
        {label}
        <textarea
          rows={3}
          value={items.join('\n')}
          placeholder={t('pages.listHint')}
          onChange={(event) =>
            onChange(
              event.target.value
                .split('\n')
                .map((line) => line.trim())
                .filter((line) => line !== ''),
            )
          }
        />
        <span className="muted small">{t('pages.listHint')}</span>
      </div>
    );
  }

  if (spec.kind === 'qa') {
    const items = Array.isArray(value) ? (value as { question: string; answer: string }[]) : [];

    return (
      <div className="stack__field">
        {label}
        {items.map((item, index) => (
          <div key={index} className="cell-inline">
            <input
              value={item.question}
              placeholder={t('pages.question')}
              aria-label={t('pages.question')}
              onChange={(event) =>
                onChange(
                  items.map((entry, at) =>
                    at === index ? { ...entry, question: event.target.value } : entry,
                  ),
                )
              }
            />
            <input
              value={item.answer}
              placeholder={t('pages.answer')}
              aria-label={t('pages.answer')}
              onChange={(event) =>
                onChange(
                  items.map((entry, at) =>
                    at === index ? { ...entry, answer: event.target.value } : entry,
                  ),
                )
              }
            />
            <button
              type="button"
              className="chip__remove"
              aria-label={t('pages.removeItem')}
              onClick={() => onChange(items.filter((_, at) => at !== index))}
            >
              ×
            </button>
          </div>
        ))}

        <button
          type="button"
          className="button button--quiet button--compact"
          onClick={() => onChange([...items, { question: '', answer: '' }])}
        >
          {t('pages.addItem')}
        </button>
      </div>
    );
  }

  return (
    <label className="stack__field">
      {label}
      <input
        value={text}
        maxLength={spec.kind === 'url' ? 2000 : 300}
        placeholder={spec.kind === 'url' ? 'https://' : undefined}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}
